/* The Sound tab's two charts.
 *
 * They are deliberately one picture: a shared time window, a shared crosshair,
 * and a range taken from the data rather than assumed. Hovering either one
 * marks the same instant on both, because the question being asked is almost
 * always "what did the spectrum look like when the level did that".
 *
 * Everything here is dBFS. Whether it may be called dB re 1 uPa is the server's
 * decision (calibration.py) and is printed, never computed.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { LUT } from '../dsp'
import {
  DPR, fit, fmtSpan, stamp, tickLabel, timeTicks,
  type Selector, type View, type Viewport,
} from '../charts'

export interface LevelPoint { t: number; bb: number; bands: number[] }
export interface LevelMinute {
  t: number
  bb: number | null
  l5: number | null
  l50: number | null
  l95: number | null
  peak: number | null
  sel: number | null
  verdict: string | null
  score: number | null
  keep_audio: boolean | null
  /** The converter ran out of range in this minute and nothing recovered it,
   *  so `peak` is a FLOOR and not a value -- its amplitude is unrecoverable
   *  and its spectrum is manufactured by the clipping. Decided on the server
   *  in levels._bound, which also collapses away the fact that there are two
   *  channels involved. Absent on every roll-up written before 2026-08-13. */
  at_least?: boolean
  /** The level is real and came from the low-gain channel. Carried so a series
   *  cannot silently mix two measurement paths; nothing customer-facing shows
   *  it, because "which channel" is a fact about our wiring. */
  recovered?: boolean
}
export interface LevelEvent {
  t: number
  duration_s: number | null
  verdict: string
  score: number | null
  drivers_hz: number[]
  reason: string | null
  keep_audio: boolean | null
  peak_db: number | null
  sel_db: number | null
  src: string | null
}

/** A gap longer than this is a hole in the recording, not a slow minute.
 *  R5.2: availability comes from what actually arrived, so the line breaks
 *  rather than being drawn straight through an outage. */
const GAP_S = 150

const REVEAL_MS = 620
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

function hz(v: number) {
  return v >= 1000 ? `${v / 1000}k` : `${v}`
}

function useReduced() {
  const [calm, setCalm] = useState(false)
  useEffect(() => {
    setCalm(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
  }, [])
  return calm
}

/** 0 -> 1 whenever `key` changes, so new data wipes in from the left.
 *
 *  A chart that simply appears reads as a page load; one that draws in reads as
 *  a measurement arriving, which is what it is. Skipped entirely under
 *  prefers-reduced-motion rather than merely shortened.
 */
function useReveal(key: unknown): number {
  const [p, setP] = useState(1)
  const calm = useReduced()
  useEffect(() => {
    if (calm) { setP(1); return }
    let raf = 0
    let start = 0
    setP(0)
    const step = (now: number) => {
      if (!start) start = now
      const k = Math.min(1, (now - start) / REVEAL_MS)
      setP(easeOutCubic(k))
      if (k < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [key, calm])
  return p
}

/* ------------------------------------------------------------- interaction */

/** Drag to pan, wheel to zoom, pinch to zoom, and a crosshair that follows.
 *
 *  `touch-action: pan-y` in the stylesheet is what makes this work on a phone:
 *  the browser keeps vertical page scrolling, and horizontal drags and pinches
 *  arrive here instead. Without it the chart either cannot be panned or the
 *  page cannot be scrolled, and on a device used one-handed on a quay both of
 *  those are the wrong answer.
 */
function usePointer(
  ref: React.RefObject<HTMLDivElement | null>,
  vp: Viewport,
  setHoverT: (t: number | null) => void,
  onHoverY?: (frac: number | null) => void,
  sel?: Selector,
) {
  const vpRef = useRef(vp)
  vpRef.current = vp
  // Through a ref for the same reason as the viewport: this object is rebuilt
  // on every render, and in the dependency array it would tear down and
  // re-register every listener mid-gesture.
  const selRef = useRef(sel)
  selRef.current = sel

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const active = new Map<number, { x: number; y: number }>()
    let dragging = false
    let selecting = false
    let lastX = 0
    let pinch = 0
    let moved = false

    const box = () => el.getBoundingClientRect()
    const fracX = (cx: number) => {
      const r = box()
      return Math.min(1, Math.max(0, (cx - r.left) / Math.max(1, r.width)))
    }
    const timeAt = (cx: number) => {
      const v = vpRef.current.view
      return v.t0 + fracX(cx) * (v.t1 - v.t0)
    }

    /** If the pointer is on a selection handle, the time of the OTHER edge.
     *
     *  Returned as the anchor for a fresh drag, so grabbing a handle and
     *  drawing a new selection are one code path.
     */
    const grabbedEdge = (s: Selector, cx: number): number | null => {
      if (!s.sel) return null
      const v = vpRef.current.view
      const r = box()
      const xOf = (t: number) =>
        r.left + ((t - v.t0) / Math.max(1, v.t1 - v.t0)) * r.width
      // Generous, and in CSS pixels: --touch is 44px in this stylesheet and a
      // handle drawn 3px wide still has to be catchable with a thumb.
      const near = 14
      if (Math.abs(cx - xOf(s.sel.t0)) < near) return s.sel.t1
      if (Math.abs(cx - xOf(s.sel.t1)) < near) return s.sel.t0
      return null
    }

    const down = (e: PointerEvent) => {
      active.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (active.size === 1) {
        const s = selRef.current
        // Shift is the desktop shortcut; the mode toggle is what makes this
        // reachable on a phone, where there is no shift key and one finger is
        // the only gesture there is.
        selecting = !!s && (s.mode === 'select' || e.shiftKey)
        dragging = !selecting
        moved = false
        lastX = e.clientX
        el.setPointerCapture(e.pointerId)
        if (selecting) {
          // Grabbing an existing edge is the same gesture as drawing a new
          // selection, anchored at the far edge instead of at the pointer. So
          // adjusting one end costs no extra machinery, and -- more to the
          // point -- a near-miss on a handle widens the selection rather than
          // silently discarding it and starting a new one, which is the
          // failure that makes brush interfaces feel hostile.
          const g = grabbedEdge(s!, e.clientX)
          s!.begin(g ?? timeAt(e.clientX))
          if (g !== null) s!.extend(timeAt(e.clientX))
        }
      } else if (active.size === 2) {
        // A second finger cancels the pan and starts a pinch, rather than the
        // two fighting for the same gesture. A selection in progress is
        // abandoned for the same reason -- and abandoned rather than committed,
        // because the first finger's travel before the second landed is not
        // something anyone chose.
        dragging = false
        if (selecting) { selecting = false; selRef.current?.clear() }
        const [a, b] = [...active.values()]
        pinch = Math.abs(a!.x - b!.x) || 1
      }
    }

    const move = (e: PointerEvent) => {
      if (active.has(e.pointerId)) active.set(e.pointerId, { x: e.clientX, y: e.clientY })

      if (active.size === 2) {
        const [a, b] = [...active.values()]
        const d = Math.abs(a!.x - b!.x) || 1
        if (pinch > 0) {
          const r = box()
          const mid = (a!.x + b!.x) / 2
          vpRef.current.zoomAt(pinch / d, Math.min(1, Math.max(0,
            (mid - r.left) / Math.max(1, r.width))))
        }
        pinch = d
        setHoverT(null)
        return
      }

      if (selecting) {
        if (Math.abs(e.clientX - lastX) > 0) moved = true
        selRef.current?.extend(timeAt(e.clientX))
        // The crosshair stays live while brushing: it is the only readout of
        // where the moving edge actually is.
        setHoverT(timeAt(e.clientX))
        return
      }

      if (dragging) {
        const dx = e.clientX - lastX
        if (Math.abs(dx) > 0) moved = true
        lastX = e.clientX
        vpRef.current.panByFraction(-dx / Math.max(1, box().width))
        setHoverT(null)
        return
      }

      setHoverT(timeAt(e.clientX))
      if (onHoverY) {
        const r = box()
        onHoverY(Math.min(1, Math.max(0, (e.clientY - r.top) / Math.max(1, r.height))))
      }
    }

    const up = (e: PointerEvent) => {
      active.delete(e.pointerId)
      if (active.size < 2) pinch = 0
      if (active.size === 0) {
        if (selecting) { selRef.current?.commit(); selecting = false }
        dragging = false
        // A drag that moved should not also register as a click on whatever
        // happened to be under the finger when it stopped.
        if (moved) el.dataset.dragged = '1'
        else delete el.dataset.dragged
      }
    }

    const leave = () => {
      if (!dragging && !selecting) { setHoverT(null); onHoverY?.(null) }
    }

    // Non-passive: zooming has to stop the page scrolling underneath it.
    const wheel = (e: WheelEvent) => {
      e.preventDefault()
      // ctrlKey is how a trackpad pinch arrives. Both zoom; the pinch is just
      // reported with a larger delta, so it is scaled down to match.
      const k = Math.exp((e.ctrlKey ? e.deltaY * 0.6 : e.deltaY) * 0.0022)
      vpRef.current.zoomAt(k, fracX(e.clientX))
    }

    el.addEventListener('pointerdown', down)
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
    el.addEventListener('pointerleave', leave)
    el.addEventListener('wheel', wheel, { passive: false })
    return () => {
      el.removeEventListener('pointerdown', down)
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerup', up)
      el.removeEventListener('pointercancel', up)
      el.removeEventListener('pointerleave', leave)
      el.removeEventListener('wheel', wheel)
    }
  }, [ref, setHoverT, onHoverY])
}

/** Paint the brushed range onto an overlay canvas.
 *
 *  Shared by both charts so the band lands on exactly the same pixels in each.
 *  They are one picture, and a selection that disagreed between them by even a
 *  pixel would be read as two different answers to the same question.
 *
 *  Dims outside rather than tinting inside: a tint over the spectrogram would
 *  shift every colour in the selection, and those colours are the measurement.
 */
function paintSelection(
  ctx: CanvasRenderingContext2D, w: number, h: number, view: View,
  range: { t0: number; t1: number } | null, settled: boolean,
) {
  if (!range) return
  const span = Math.max(1, view.t1 - view.t0)
  const xOf = (t: number) => ((t - view.t0) / span) * w
  const a = Math.min(xOf(range.t0), xOf(range.t1))
  const b = Math.max(xOf(range.t0), xOf(range.t1))
  if (b < 0 || a > w) return

  ctx.save()
  ctx.fillStyle = 'rgba(8,46,34,0.58)'
  ctx.fillRect(0, 0, Math.max(0, Math.min(a, w)), h)
  ctx.fillRect(Math.max(0, Math.min(b, w)), 0, w, h)

  ctx.strokeStyle = settled ? 'rgba(180,217,199,0.95)' : 'rgba(180,217,199,0.6)'
  ctx.lineWidth = settled ? 2 : 1
  for (const x of [a, b]) {
    if (x < 0 || x > w) continue
    const px = Math.round(x) + 0.5
    ctx.beginPath()
    ctx.moveTo(px, 0)
    ctx.lineTo(px, h)
    ctx.stroke()
    // Grips, only once the selection is committed: something to aim a thumb
    // at, and a signal that the edge can be moved rather than only redrawn.
    if (settled) {
      ctx.fillStyle = 'rgba(180,217,199,0.95)'
      ctx.fillRect(px - 2.5, h / 2 - 9, 5, 18)
    }
  }
  ctx.restore()
}

/** Where to put a readout so it never leaves the card. */
function tipStyle(frac: number): React.CSSProperties {
  const right = frac > 0.62
  return {
    left: `${frac * 100}%`,
    transform: right ? 'translateX(calc(-100% - 14px))' : 'translateX(14px)',
  }
}

function nearest<T extends { t: number }>(rows: T[], t: number): T | null {
  if (!rows.length) return null
  let lo = 0
  let hi = rows.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (rows[mid]!.t < t) lo = mid + 1
    else hi = mid
  }
  const a = rows[lo]!
  const b = rows[Math.max(0, lo - 1)]!
  return Math.abs(a.t - t) <= Math.abs(b.t - t) ? a : b
}

/* ------------------------------------------------------ band spectrogram */

/** Level per frequency band over time.
 *
 *  Eleven bands with stated edges, taken from the node's own per-second
 *  analysis. The node also ships a 2168-value spectrum, but with no
 *  accompanying edge array -- so its frequency axis could only be guessed at,
 *  and a guessed frequency axis is a number that looks measured. Eleven honest
 *  bands beat two thousand unlabelled ones.
 */
export function BandSpectrogram({
  points, bandsHz, vp, range, hoverT, setHoverT, height = 190, sel,
}: {
  sel?: Selector
  points: LevelPoint[]
  bandsHz: number[]
  vp: Viewport
  range: [number, number]
  hoverT: number | null
  setHoverT: (t: number | null) => void
  height?: number
}) {
  const nb = Math.max(1, bandsHz.length - 1)
  const wrap = useRef<HTMLDivElement>(null)
  const base = useRef<HTMLCanvasElement>(null)
  const over = useRef<HTMLCanvasElement>(null)
  const buf = useRef<HTMLCanvasElement | null>(null)
  const [hoverY, setHoverY] = useState<number | null>(null)
  const reveal = useReveal(points)
  const { view } = vp
  const [minDb, maxDb] = range

  usePointer(wrap, vp, setHoverT, setHoverY, sel)
  // Primitives, not the Selector: it is rebuilt every render, so in a
  // dependency array it would repaint the overlay on every keystroke anywhere
  // on the page.
  const selT0 = sel?.sel?.t0 ?? null
  const selT1 = sel?.sel?.t1 ?? null
  const liveT0 = sel?.live?.t0 ?? null
  const liveT1 = sel?.live?.t1 ?? null

  // Painted as one image rather than as thousands of rectangles. At a week of
  // per-second data a fillRect per band per column is tens of thousands of
  // draw calls a frame while dragging, which is the difference between a chart
  // that follows your finger and one that stutters behind it.
  useEffect(() => {
    const canvas = base.current
    if (!canvas) return
    const f = fit(canvas, height)
    if (!f) return
    const { ctx, w, h } = f

    ctx.fillStyle = '#04150f'
    ctx.fillRect(0, 0, w, h)
    if (!points.length || w < 2) return

    const span = Math.max(1, view.t1 - view.t0)
    const cols = Math.max(1, Math.round(w))
    if (!buf.current) buf.current = document.createElement('canvas')
    const off = buf.current
    off.width = cols
    off.height = nb
    const octx = off.getContext('2d')
    if (!octx) return
    const img = octx.createImageData(cols, nb)
    const px = img.data

    let idx = 0
    for (let x = 0; x < cols; x++) {
      const t = view.t0 + ((x + 0.5) / cols) * span
      while (idx < points.length - 1 && points[idx + 1]!.t <= t) idx++
      let p = points[idx]!
      const nxt = points[idx + 1]
      if (nxt && Math.abs(nxt.t - t) < Math.abs(p.t - t)) p = nxt
      // Outside the data, or inside a hole in it: left as background so an
      // outage reads as an outage instead of as a stretched column.
      if (Math.abs(p.t - t) > GAP_S) continue

      for (let b = 0; b < nb; b++) {
        const db = p.bands[b]
        if (db === undefined) continue
        let k = (db - minDb) / Math.max(1, maxDb - minDb)
        k = k < 0 ? 0 : k > 1 ? 1 : k
        const c = (k * 255) | 0
        // Bands run low to high; row 0 is the top, so the axis reads upward.
        const o = ((nb - 1 - b) * cols + x) * 4
        px[o] = LUT[c * 3]!
        px[o + 1] = LUT[c * 3 + 1]!
        px[o + 2] = LUT[c * 3 + 2]!
        px[o + 3] = 255
      }
    }
    octx.putImageData(img, 0, 0)

    ctx.save()
    ctx.beginPath()
    ctx.rect(0, 0, w * reveal, h)
    ctx.clip()
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(off, 0, 0, cols, nb, 0, 0, w, h)
    ctx.restore()

    // Band separators, barely there. They stop eleven bands reading as a
    // continuous spectrum, which would imply a resolution this does not have.
    ctx.strokeStyle = 'rgba(4,21,15,0.5)'
    ctx.lineWidth = 1
    for (let b = 1; b < nb; b++) {
      const y = Math.round((b / nb) * h) + 0.5
      ctx.beginPath()
      ctx.moveTo(0, y)
      ctx.lineTo(w * reveal, y)
      ctx.stroke()
    }
  }, [points, nb, view.t0, view.t1, minDb, maxDb, height, reveal])

  // Crosshair on its own canvas, so following the pointer never repaints the
  // spectrogram underneath it.
  useEffect(() => {
    const canvas = over.current
    if (!canvas) return
    const f = fit(canvas, height)
    if (!f) return
    const { ctx, w, h } = f
    ctx.clearRect(0, 0, w, h)

    // Under the crosshair: the crosshair is a pointer position and the
    // selection is a stated range, and the transient one belongs on top.
    const live = liveT0 !== null && liveT1 !== null ? { t0: liveT0, t1: liveT1 } : null
    const settled = selT0 !== null && selT1 !== null ? { t0: selT0, t1: selT1 } : null
    paintSelection(ctx, w, h, view, live ?? settled, !live && !!settled)

    if (hoverT === null) return
    const span = Math.max(1, view.t1 - view.t0)
    const x = Math.round(((hoverT - view.t0) / span) * w) + 0.5
    if (x < 0 || x > w) return
    ctx.strokeStyle = 'rgba(230,241,234,0.72)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, h)
    ctx.stroke()
    if (hoverY !== null) {
      const b = Math.min(nb - 1, Math.floor(hoverY * nb))
      ctx.strokeStyle = 'rgba(230,241,234,0.5)'
      ctx.strokeRect(0, Math.round((b / nb) * h) + 0.5, w, Math.round(h / nb))
    }
  }, [hoverT, hoverY, view.t0, view.t1, height, nb,
      selT0, selT1, liveT0, liveT1])

  const hovered = hoverT === null ? null : nearest(points, hoverT)
  const inRange = hovered && Math.abs(hovered.t - (hoverT ?? 0)) <= GAP_S
  const band = hoverY === null ? null : Math.min(nb - 1, Math.floor((1 - hoverY) * nb))
  const frac = hoverT === null ? 0 : (hoverT - view.t0) / Math.max(1, view.t1 - view.t0)

  return (
    <div className="chart" ref={wrap} style={{ height }}
      role="img"
      aria-label={`Level by frequency band, ${bandsHz[0] ?? 0} to ${bandsHz[bandsHz.length - 1] ?? 0} hertz. Drag to pan, pinch or scroll to zoom.`}>
      <canvas ref={base} className="chart-canvas" />
      <canvas ref={over} className="chart-canvas chart-over" />
      <div className="spec-axis" aria-hidden="true">
        {bandsHz.slice(0, -1).map((f, i) => (
          <span key={f} style={{ top: `${(1 - (i + 0.5) / nb) * 100}%` }}>{hz(f)}</span>
        ))}
      </div>
      {inRange && band !== null && hovered && (
        <div className="chart-tip" style={tipStyle(frac)}>
          <div className="chart-tip-t">{stamp(hovered.t)}</div>
          <div className="chart-tip-row">
            <span>{hz(bandsHz[band] ?? 0)}–{hz(bandsHz[band + 1] ?? 0)} Hz</span>
            <strong className="num">
              {hovered.bands[band] !== undefined
                ? `${hovered.bands[band]!.toFixed(1)} dB`
                : '—'}
            </strong>
          </div>
          <div className="chart-tip-row faint">
            <span>broadband</span>
            <span className="num">{hovered.bb.toFixed(1)} dB</span>
          </div>
        </div>
      )}
    </div>
  )
}

/* --------------------------------------------------------- level history */

/** Broadband level with its percentile envelope, and every event marked.
 *
 *  L5 to L95 shaded is how underwater noise is normally reported: the median
 *  says what it usually sounds like, the top of the band says how loud the
 *  loud moments were, and the gap between them says whether the place is
 *  steady or spiky. A single mean line hides all three.
 */
export function LevelHistory({
  minutes, events, vp, range, hoverT, setHoverT, height = 190, onPickEvent, sel,
}: {
  sel?: Selector
  minutes: LevelMinute[]
  events: LevelEvent[]
  vp: Viewport
  range: [number, number]
  hoverT: number | null
  setHoverT: (t: number | null) => void
  height?: number
  onPickEvent?: (e: LevelEvent) => void
}) {
  const wrap = useRef<HTMLDivElement>(null)
  const base = useRef<HTMLCanvasElement>(null)
  const over = useRef<HTMLCanvasElement>(null)
  const reveal = useReveal(minutes)
  const { view } = vp
  const [minDb, maxDb] = range

  usePointer(wrap, vp, setHoverT, undefined, sel)
  const selT0 = sel?.sel?.t0 ?? null
  const selT1 = sel?.sel?.t1 ?? null
  const liveT0 = sel?.live?.t0 ?? null
  const liveT1 = sel?.live?.t1 ?? null

  useEffect(() => {
    const canvas = base.current
    if (!canvas) return
    const f = fit(canvas, height)
    if (!f) return
    const { ctx, w, h } = f
    ctx.clearRect(0, 0, w, h)
    if (!minutes.length) return

    const span = Math.max(1, view.t1 - view.t0)
    const X = (t: number) => ((t - view.t0) / span) * w
    const Y = (db: number) => h - ((db - minDb) / Math.max(1, maxDb - minDb)) * h

    // Guides on round numbers taken from the range, not every 20 dB from a
    // constant that no longer describes anything.
    const stepDb = Math.max(5, Math.round((maxDb - minDb) / 4 / 5) * 5)
    ctx.font = '11px "Plus Jakarta Sans", sans-serif'
    for (let db = Math.ceil(minDb / stepDb) * stepDb; db <= maxDb; db += stepDb) {
      const y = Math.round(Y(db)) + 0.5
      ctx.strokeStyle = 'rgba(180,217,199,0.12)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(0, y)
      ctx.lineTo(w, y)
      ctx.stroke()
      ctx.fillStyle = 'rgba(185,206,195,0.7)'
      ctx.fillText(`${db}`, 4, y - 4)
    }

    ctx.save()
    ctx.beginPath()
    ctx.rect(0, 0, w * reveal, h)
    ctx.clip()

    // Only what is on screen, with one sample of margin so the line enters and
    // leaves the frame instead of stopping at the edge.
    const vis = minutes.filter((m) => m.t >= view.t0 - GAP_S && m.t <= view.t1 + GAP_S)

    const runs: LevelMinute[][] = []
    let run: LevelMinute[] = []
    for (const m of vis) {
      if (run.length && m.t - run[run.length - 1]!.t > GAP_S) { runs.push(run); run = [] }
      run.push(m)
    }
    if (run.length) runs.push(run)

    for (const r of runs) {
      const withPct = r.filter((m) => m.l5 !== null && m.l95 !== null)
      if (withPct.length > 1) {
        ctx.beginPath()
        withPct.forEach((m, i) => {
          const x = X(m.t), y = Y(m.l5!)
          i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)
        })
        for (let i = withPct.length - 1; i >= 0; i--) {
          ctx.lineTo(X(withPct[i]!.t), Y(withPct[i]!.l95!))
        }
        ctx.closePath()
        ctx.fillStyle = 'rgba(180,217,199,0.16)'
        ctx.fill()
      }

      const path = (get: (m: LevelMinute) => number | null) => {
        const p = new Path2D()
        let started = false
        for (const m of r) {
          const v = get(m)
          if (v === null || !Number.isFinite(v)) continue
          const x = X(m.t), y = Y(v)
          started ? p.lineTo(x, y) : p.moveTo(x, y)
          started = true
        }
        return started ? p : null
      }

      // A wash under the broadband line. It gives the chart a body at a glance
      // -- you can see the shape of a night from across the room -- without
      // adding a second encoding of the same number.
      const bb = path((m) => m.bb)
      if (bb) {
        const area = new Path2D(bb)
        const first = r.find((m) => m.bb !== null)
        const last = [...r].reverse().find((m) => m.bb !== null)
        if (first && last) {
          area.lineTo(X(last.t), h)
          area.lineTo(X(first.t), h)
          area.closePath()
          const g = ctx.createLinearGradient(0, 0, 0, h)
          g.addColorStop(0, 'rgba(180,217,199,0.26)')
          g.addColorStop(1, 'rgba(180,217,199,0)')
          ctx.fillStyle = g
          ctx.fill(area)
        }
      }

      const l50 = path((m) => m.l50)
      if (l50) {
        ctx.strokeStyle = 'rgba(180,217,199,0.5)'
        ctx.lineWidth = 1
        ctx.setLineDash([3, 3])
        ctx.stroke(l50)
        ctx.setLineDash([])
      }
      if (bb) {
        ctx.lineJoin = 'round'
        ctx.lineCap = 'round'
        ctx.strokeStyle = '#b4d9c7'
        ctx.lineWidth = 1.9
        ctx.stroke(bb)
      }
    }
    ctx.restore()

    // Events along the top. The only red on the chart, and rare by nature --
    // which is the ratio the brand asks for, arrived at honestly.
    for (const e of events) {
      const x = Math.round(X(e.t)) + 0.5
      if (x < -2 || x > w + 2 || x > w * reveal) continue
      const strong = e.verdict === 'event'
      ctx.strokeStyle = strong ? '#e63926' : 'rgba(230,57,38,0.45)'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x, strong ? 11 : 6)
      ctx.stroke()
    }
  }, [minutes, events, view.t0, view.t1, minDb, maxDb, height, reveal])

  useEffect(() => {
    const canvas = over.current
    if (!canvas) return
    const f = fit(canvas, height)
    if (!f) return
    const { ctx, w, h } = f
    ctx.clearRect(0, 0, w, h)

    const live = liveT0 !== null && liveT1 !== null ? { t0: liveT0, t1: liveT1 } : null
    const settled = selT0 !== null && selT1 !== null ? { t0: selT0, t1: selT1 } : null
    paintSelection(ctx, w, h, view, live ?? settled, !live && !!settled)

    if (hoverT === null || !minutes.length) return
    const span = Math.max(1, view.t1 - view.t0)
    const m = nearest(minutes, hoverT)
    if (!m || Math.abs(m.t - hoverT) > GAP_S) return
    const x = Math.round(((m.t - view.t0) / span) * w) + 0.5
    if (x < 0 || x > w) return

    ctx.strokeStyle = 'rgba(230,241,234,0.72)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, h)
    ctx.stroke()

    if (m.bb !== null && Number.isFinite(m.bb)) {
      const y = h - ((m.bb - minDb) / Math.max(1, maxDb - minDb)) * h
      ctx.fillStyle = '#04150f'
      ctx.strokeStyle = '#b4d9c7'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(x, y, 4, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    }
  }, [hoverT, minutes, view.t0, view.t1, minDb, maxDb, height,
      selT0, selT1, liveT0, liveT1])

  const m = hoverT === null ? null : nearest(minutes, hoverT)
  const near = m && Math.abs(m.t - (hoverT ?? 0)) <= GAP_S ? m : null
  const ev = hoverT === null ? null : nearest(events, hoverT)
  const nearEv = ev && Math.abs(ev.t - (hoverT ?? 0)) <= 60 ? ev : null
  const frac = hoverT === null ? 0 : (hoverT - view.t0) / Math.max(1, view.t1 - view.t0)

  const click = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onPickEvent || !events.length) return
    if (e.currentTarget.dataset.dragged) return   // that was a pan, not a click
    const r = e.currentTarget.getBoundingClientRect()
    const t = view.t0 + ((e.clientX - r.left) / r.width) * (view.t1 - view.t0)
    const best = nearest(events, t)
    const perPx = (view.t1 - view.t0) / r.width
    if (best && Math.abs(best.t - t) < perPx * 14) onPickEvent(best)
  }

  return (
    <div className="chart" ref={wrap} style={{ height, cursor: onPickEvent ? 'crosshair' : 'grab' }}
      onClick={click}
      role="img"
      aria-label={`Broadband level over time, ${minDb} to ${maxDb} decibels full scale. Drag to pan, pinch or scroll to zoom.`}>
      <canvas ref={base} className="chart-canvas" />
      <canvas ref={over} className="chart-canvas chart-over" />
      {near && (
        <div className="chart-tip" style={tipStyle(frac)}>
          <div className="chart-tip-t">{stamp(near.t)}</div>
          <div className="chart-tip-row">
            <span>broadband</span>
            <strong className="num">{near.bb?.toFixed(1) ?? '—'} dB</strong>
          </div>
          {near.l50 !== null && (
            <div className="chart-tip-row faint">
              <span>L5 / L50 / L95</span>
              <span className="num">
                {near.l5?.toFixed(0) ?? '—'} / {near.l50.toFixed(0)} / {near.l95?.toFixed(0) ?? '—'}
              </span>
            </div>
          )}
          {near.peak !== null && (
            <div className="chart-tip-row faint">
              {/* A railed minute is a floor, and the tooltip is where anyone
                  reading a level off this chart actually reads it. */}
              <span>{near.at_least ? 'peak (clipped)' : 'peak'}</span>
              <span className="num">
                {near.at_least ? '≥ ' : ''}{near.peak.toFixed(1)} dB
              </span>
            </div>
          )}
          {nearEv && (
            <div className="chart-tip-ev">
              {nearEv.verdict}
              {nearEv.score != null && <span className="num"> · {nearEv.score.toFixed(1)}</span>}
              {onPickEvent && <span className="faint"> · click to open</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------- time axis */

/** Shared time axis, drawn once under both charts so they read as one picture. */
export function TimeAxis({ view }: { view: View }) {
  const span = Math.max(1, view.t1 - view.t0)
  const ticks = useMemo(() => timeTicks(view.t0, view.t1), [view.t0, view.t1])
  return (
    <div className="time-axis" aria-hidden="true">
      {ticks.map((t) => ((t - view.t0) / span))
        // Labels are centred on their tick, so one within a few percent of an
        // edge would be drawn half outside the card. Dropping it is better
        // than showing half a timestamp.
        .map((f, i) => (f < 0.035 || f > 0.965 ? null : (
          <span key={ticks[i]} style={{ left: `${f * 100}%` }}>
            {tickLabel(ticks[i]!, span)}
          </span>
        )))}
    </div>
  )
}

/* ---------------------------------------------------------- availability */

export interface AvailabilityHour {
  t: number
  cloud_seconds: number
  cloud_missing_seconds: number
  node_seconds: number
  node_uploaded_seconds: number
  node_bytes_estimate: number | null
}

export interface NodeSnapshot {
  observed_utc: string | null
  age_seconds: number | null
  generation: number | null
  retention_hours: number | null
  oldest_present_utc: string | null
  horizon_projected_utc: string | null
  uploads_audio: boolean
}

const HOUR_S = 3600

/** Where an hour's audio is, along the same axis as the charts above it.
 *
 *  `DayTimeline` answers this for one day and this is the same three facts
 *  re-laid against a viewport -- deliberately the same colours and the same
 *  words, because an operator who has learned that strip should not have to
 *  learn another.
 *
 *  Two states are added that a single day cannot produce:
 *
 *    gone     the node is no longer holding it, and no request can reach it
 *    unknown  the node has not reported since this hour, so nothing is known
 *
 *  Keeping those apart is the whole point. Drawing an unobserved hour as a gap
 *  would report data loss where there is only a stale snapshot, and drawing it
 *  as available would offer a button that cannot work. The boundary between
 *  them is `oldest_present_utc` -- observed, not computed from a retention
 *  figure, because the janitor caps segment count rather than wall-clock age
 *  and the two differ by hours on a node that has ever missed a recording.
 */
export function AvailabilityStrip({
  hours, node, vp, sel, height = 38,
}: {
  hours: AvailabilityHour[]
  node: NodeSnapshot
  vp: Viewport
  sel?: Selector
  height?: number
}) {
  const wrap = useRef<HTMLDivElement>(null)
  const base = useRef<HTMLCanvasElement>(null)
  const over = useRef<HTMLCanvasElement>(null)
  const { view } = vp
  const [hoverT, setHoverT] = useState<number | null>(null)

  usePointer(wrap, vp, setHoverT, undefined, sel)
  const selT0 = sel?.sel?.t0 ?? null
  const selT1 = sel?.sel?.t1 ?? null
  const liveT0 = sel?.live?.t0 ?? null
  const liveT1 = sel?.live?.t1 ?? null

  const byHour = useMemo(() => {
    const m = new Map<number, AvailabilityHour>()
    for (const h of hours) m.set(Math.floor(h.t / HOUR_S) * HOUR_S, h)
    return m
  }, [hours])

  const oldest = node.oldest_present_utc
    ? new Date(node.oldest_present_utc).getTime() / 1000 : null
  const observed = node.observed_utc
    ? new Date(node.observed_utc).getTime() / 1000 : null

  useEffect(() => {
    const canvas = base.current
    if (!canvas) return
    const f = fit(canvas, height)
    if (!f) return
    const { ctx, w, h } = f
    ctx.clearRect(0, 0, w, h)

    const span = Math.max(1, view.t1 - view.t0)
    const X = (t: number) => ((t - view.t0) / span) * w
    const first = Math.floor(view.t0 / HOUR_S) * HOUR_S
    // At the 30-day range this is 720 rects, which is far below the threshold
    // that pushed the spectrogram to ImageData -- so the simple loop is the
    // right call here and the complexity is not worth buying.
    for (let t = first; t < view.t1; t += HOUR_S) {
      const x0 = X(t)
      const x1 = X(t + HOUR_S)
      const bw = Math.max(1, x1 - x0 - 1)
      if (x1 < 0 || x0 > w) continue
      const row = byHour.get(t)

      if (!row || (!row.node_seconds && !row.cloud_seconds)) {
        if (observed !== null && t >= observed - HOUR_S) {
          // Unknown: not reported on yet. Faint dots, deliberately not the gap
          // hatch -- "we have not heard" must not look like "nothing is there".
          ctx.fillStyle = 'rgba(147,171,159,0.30)'
          for (let y = 3; y < h - 2; y += 5) ctx.fillRect(x0 + bw / 2, y, 1.5, 1.5)
        } else if (oldest !== null && t < oldest) {
          // Gone. A baseline mark rather than blank: the hour existed, and its
          // audio is now unreachable, which is a fact worth drawing.
          ctx.fillStyle = 'rgba(147,171,159,0.22)'
          ctx.fillRect(x0, h - 2, bw, 2)
        } else {
          // A real gap inside the observed, still-held window: nothing was
          // captured. Same hatch as the day timeline.
          ctx.fillStyle = 'rgba(147,171,159,0.13)'
          ctx.fillRect(x0, 0, bw, h)
        }
        continue
      }

      // Proportional, never squared off. The oldest hour on a card is normally
      // half-eaten and the current one is still filling; both are the steady
      // state and rounding them to full blocks would misreport what is held.
      const nodeH = Math.round((row.node_seconds / HOUR_S) * h)
      if (nodeH > 0) {
        ctx.fillStyle = 'rgba(180,217,199,0.30)'
        ctx.fillRect(x0, h - nodeH, bw, nodeH)
        ctx.fillStyle = 'rgba(180,217,199,0.55)'
        ctx.fillRect(x0, h - nodeH, bw, 1)
      }
      const cloudH = Math.round((row.cloud_seconds / HOUR_S) * h)
      if (cloudH > 0) {
        ctx.fillStyle = '#b4d9c7'
        ctx.fillRect(x0, h - cloudH, bw, cloudH)
      }
      if (row.cloud_missing_seconds > 0) {
        // The index has it and storage does not. Red is reserved for detection
        // everywhere else on this page, and this is the one other thing that
        // genuinely needs alarm: it is data believed to exist that does not.
        ctx.fillStyle = 'rgba(230,57,38,0.55)'
        ctx.fillRect(x0, 0, bw, 3)
      }
    }

    // The retention boundary, labelled. It is the most consequential line on
    // the page: left of it, no request can succeed.
    if (oldest !== null && oldest > view.t0 && oldest < view.t1) {
      const x = Math.round(X(oldest)) + 0.5
      ctx.strokeStyle = 'rgba(230,241,234,0.45)'
      ctx.lineWidth = 1
      ctx.setLineDash([3, 3])
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x, h)
      ctx.stroke()
      ctx.setLineDash([])
    }
  }, [byHour, view.t0, view.t1, height, oldest, observed])

  useEffect(() => {
    const canvas = over.current
    if (!canvas) return
    const f = fit(canvas, height)
    if (!f) return
    const { ctx, w, h } = f
    ctx.clearRect(0, 0, w, h)
    const live = liveT0 !== null && liveT1 !== null ? { t0: liveT0, t1: liveT1 } : null
    const settled = selT0 !== null && selT1 !== null ? { t0: selT0, t1: selT1 } : null
    paintSelection(ctx, w, h, view, live ?? settled, !live && !!settled)
  }, [view.t0, view.t1, height, selT0, selT1, liveT0, liveT1])

  const inView = hours.filter((r) => r.t >= view.t0 && r.t < view.t1)
  const stored = inView.reduce((a, r) => a + r.cloud_seconds, 0)
  const onNode = inView.reduce((a, r) => a + Math.max(0, r.node_seconds - r.cloud_seconds), 0)

  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="chart avail" ref={wrap} style={{ height }} role="img"
        aria-label={
          `Where audio is, hour by hour. ${Math.round(stored / 60)} minutes in `
          + `storage, ${Math.round(onNode / 60)} minutes on the node only.`
          + (node.oldest_present_utc
            ? ` The node holds nothing before ${stamp(oldest ?? 0)}.` : '')}>
        <canvas ref={base} className="chart-canvas" />
        <canvas ref={over} className="chart-canvas chart-over" />
      </div>
      {/* A coloured strip is not information to a screen reader, and the
          numbers behind it are small enough to simply state. */}
      <table className="vh">
        <caption>Audio availability by hour</caption>
        <tbody>
          {inView.map((r) => (
            <tr key={r.t}>
              <th scope="row">{stamp(r.t)}</th>
              <td>{Math.round(r.cloud_seconds / 60)} min in storage</td>
              <td>{Math.round(r.node_seconds / 60)} min on the node</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="faint tl-key" style={{ margin: 0 }}>
        <i className="sw cloud" /> in storage
        <i className="sw node" /> on the node
        <i className="sw gap" /> gap
        <i className="sw unknown" /> not reported
        {hoverT !== null && (
          <span className="num" style={{ marginLeft: 'auto' }}>{stamp(hoverT)}</span>
        )}
      </p>
    </div>
  )
}

/** The window, and the way back out of it.
 *
 *  Shown only once zoomed. A control that is always visible but usually does
 *  nothing is chrome; one that appears when it becomes true is information.
 */
export function ViewBar({ vp, full }: { vp: Viewport; full: View }) {
  const { view, zoomed, reset } = vp
  const span = view.t1 - view.t0
  const left = ((view.t0 - full.t0) / Math.max(1, full.t1 - full.t0)) * 100
  const width = (span / Math.max(1, full.t1 - full.t0)) * 100

  if (!zoomed) {
    return (
      <div className="viewbar faint">
        <span>Drag to pan · scroll or pinch to zoom</span>
      </div>
    )
  }
  return (
    <div className="viewbar">
      <div className="viewbar-rail" aria-hidden="true">
        <div className="viewbar-win" style={{ left: `${left}%`, width: `${Math.max(1, width)}%` }} />
      </div>
      <span className="num faint">{fmtSpan(span)} shown</span>
      <button className="btn ghost sm" onClick={reset}>Show everything</button>
    </div>
  )
}

/** DPR is read once per module load elsewhere; re-exported so callers that
 *  size their own canvases stay consistent with these. */
export { DPR }
